import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { numberSequenceService } from "@/services/number-sequence";

import type {
  ResetNumberSequenceReq,
  UpdateNumberSequenceReq,
} from "@artisancode/api-types";

export function useNumberSequences(workspaceId?: string) {
  return useQuery({
    queryKey: queryKeys.numberSequences.list(workspaceId),
    queryFn: () => numberSequenceService.list(workspaceId),
    retry: 1,
  });
}

export function useNumberSequenceDetail(
  moduleCode: string,
  workspaceId?: string,
) {
  return useQuery({
    queryKey: queryKeys.numberSequences.detail(moduleCode, workspaceId),
    queryFn: () =>
      numberSequenceService.getByModuleCode(moduleCode, workspaceId),
    enabled: !!moduleCode,
    retry: 1,
  });
}

export function useUpdateNumberSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      moduleCode,
      body,
      workspaceId,
    }: {
      moduleCode: string;
      body: UpdateNumberSequenceReq;
      workspaceId?: string;
    }) => numberSequenceService.update(moduleCode, body, workspaceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.numberSequences.all });
    },
  });
}

export function useResetNumberSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      moduleCode,
      body,
      workspaceId,
    }: {
      moduleCode: string;
      body?: ResetNumberSequenceReq;
      workspaceId?: string;
    }) => numberSequenceService.reset(moduleCode, body, workspaceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.numberSequences.all });
    },
  });
}

/** Next auto-number preview for one module (e.g. "sale"), straight from the server. */
export function useNextNumberPreview(moduleCode: string, workspaceId?: string) {
  const { data } = useNumberSequences(workspaceId);
  const item = data?.find((s) => s.moduleCode === moduleCode);
  return {
    preview: item?.nextPreview ?? "",
    autoGenerate: item?.autoGenerateOnCreate ?? true,
  };
}
